import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import { useMemo, useState } from 'react';

const FIELD_LABELS = {
  recipientEmail: 'Recipient',
  firstName: 'First name',
  lastName: 'Last name',
  title: 'Job title',
  seniority: 'Seniority',
  location: 'Location',
  city: 'City',
  state: 'State',
  country: 'Country',
  linkedinUrl: 'LinkedIn',
  companyName: 'Company',
  companyDomain: 'Company domain',
  industry: 'Industry',
  companySize: 'Company size',
  revenue: 'Revenue',
  hqLocation: 'HQ location',
  fundingStage: 'Funding stage',
  fundingAmount: 'Funding',
  technologies: 'Technologies',
  companyLinkedinUrl: 'Company LinkedIn',
};

function providerLabel(provider = '') {
  if (provider === 'cleanlist') return 'Cleanlist';
  if (provider === 'apollo') return 'Apollo';
  if (provider === 'tavily') return 'Web research';
  return provider || 'Research source';
}

function providerStatusMeta(result = {}) {
  if (result.status === 'success') {
    return {
      label: result.fieldsFound ? `${result.fieldsFound} useful fields` : 'Context found',
      tone: 'success',
      icon: <CheckCircleOutlineRoundedIcon fontSize="small" />,
    };
  }
  if (result.status === 'no_match') {
    return { label: 'No useful match', tone: 'default', icon: <InfoOutlinedIcon fontSize="small" /> };
  }
  if (result.status === 'not_connected') {
    return { label: 'Not connected', tone: 'default', icon: <InfoOutlinedIcon fontSize="small" /> };
  }
  if (result.status === 'skipped') {
    return { label: 'Skipped', tone: 'default', icon: <InfoOutlinedIcon fontSize="small" /> };
  }
  return { label: 'Could not check', tone: 'error', icon: <ErrorOutlineRoundedIcon fontSize="small" /> };
}

function valueText(value) {
  if (Array.isArray(value)) return value.filter(Boolean).join(', ');
  if (value && typeof value === 'object') return JSON.stringify(value);
  return String(value ?? '').trim();
}

function providerFields(data = {}) {
  return Object.entries(data || {})
    .filter(([key, value]) => !['webFindings', 'recipientEmail'].includes(key) && valueText(value))
    .map(([key, value]) => ({
      key,
      label: FIELD_LABELS[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, (m) => m.toUpperCase()),
      value: valueText(value),
    }))
    .slice(0, 14);
}

function normalizeThreadBrief(research = {}) {
  if (research?.threadBrief) return research.threadBrief;

  // Backward-compatible fallback for the first thread-first backend runs where
  // the thread brief may have been returned as the main brief.
  if (research?.brief?.source === 'thread') return research.brief;

  return null;
}

function externalBriefFor(research = {}) {
  if (research?.brief?.externalEnhancement) return research.brief.externalEnhancement;

  // Old saved research runs did not have threadBrief. Keep them readable.
  if (!research?.threadBrief && research?.brief?.usable) return research.brief;

  return null;
}

function cleanFacts(value) {
  return Array.isArray(value) ? value.map((item) => String(item || '').trim()).filter(Boolean).slice(0, 6) : [];
}

function ThreadContextCard({ brief }) {
  const facts = cleanFacts(brief?.facts);

  return (
    <Box
      sx={{
        p: 2.2,
        borderRadius: 2,
        border: '1px solid rgba(26,29,49,.10)',
        bgcolor: '#fff',
      }}
    >
      <Stack direction="row" spacing={1.1} alignItems="center">
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 1.5,
            display: 'grid',
            placeItems: 'center',
            bgcolor: 'rgba(70,201,142,.11)',
            color: 'success.dark',
            flex: '0 0 auto',
          }}
        >
          <ForumOutlinedIcon fontSize="small" />
        </Box>

        <Box minWidth={0}>
          <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography fontWeight={900}>From this email thread</Typography>
            <Chip size="small" color="success" variant="outlined" label="Primary context" />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            Nudge reads the conversation first. External enrichment never replaces what was actually said in the thread.
          </Typography>
        </Box>
      </Stack>

      {brief?.usable ? (
        <Stack spacing={1.5} sx={{ mt: 2 }}>
          {brief.openLoop && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={800}>OPEN LOOP</Typography>
              <Typography sx={{ mt: 0.3, fontWeight: 750, lineHeight: 1.5 }}>{brief.openLoop}</Typography>
            </Box>
          )}

          {brief.nextStep && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={800}>NATURAL NEXT STEP</Typography>
              <Typography variant="body2" sx={{ mt: 0.3, lineHeight: 1.55 }}>{brief.nextStep}</Typography>
            </Box>
          )}

          {brief.angle && brief.angle !== brief.openLoop && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={800}>FOLLOW-UP ANGLE</Typography>
              <Typography variant="body2" sx={{ mt: 0.3, lineHeight: 1.55 }}>{brief.angle}</Typography>
            </Box>
          )}

          {facts.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={800}>THREAD FACTS</Typography>
              <Stack spacing={0.55} sx={{ mt: 0.6 }}>
                {facts.map((fact, index) => (
                  <Typography key={`${fact}-${index}`} variant="body2" sx={{ lineHeight: 1.5 }}>
                    • {fact}
                  </Typography>
                ))}
              </Stack>
            </Box>
          )}

          {brief.reason && (
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              {brief.reason}
            </Typography>
          )}
        </Stack>
      ) : (
        <Alert severity="info" sx={{ mt: 2 }}>
          Nudge could not identify a clear open loop from the saved thread context. The draft may still use the conversation itself.
        </Alert>
      )}
    </Box>
  );
}

export function ResearchDrawer({
  open,
  onClose,
  item = null,
  research = null,
  loading = false,
  error = '',
}) {
  const [showExternal, setShowExternal] = useState(false);
  const [copied, setCopied] = useState(false);

  const threadBrief = useMemo(() => normalizeThreadBrief(research || {}), [research]);
  const externalBrief = useMemo(() => externalBriefFor(research || {}), [research]);
  const contactHistoryBrief = research?.contactHistoryBrief || null;
  const providerResults = Array.isArray(research?.providerResults) ? research.providerResults : [];
  const providersUsed = Array.isArray(research?.providers) ? research.providers : [];
  const sources = Array.isArray(research?.sources) ? research.sources : [];
  const externalMeta = research?.externalEnrichment || {};
  const externalAvailable = Boolean(
    providerResults.length ||
    providersUsed.length ||
    sources.length ||
    externalBrief
  );

  const copyThreadContext = async () => {
    const facts = cleanFacts(threadBrief?.facts);
    const parts = [
      threadBrief?.openLoop ? `Open loop: ${threadBrief.openLoop}` : '',
      threadBrief?.nextStep ? `Next step: ${threadBrief.nextStep}` : '',
      threadBrief?.angle ? `Follow-up angle: ${threadBrief.angle}` : '',
      facts.length ? `Thread facts:\n${facts.map((fact) => `- ${fact}`).join('\n')}` : '',
    ].filter(Boolean);

    if (!parts.length) return;

    await navigator.clipboard?.writeText(parts.join('\n\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ zIndex: (theme) => theme.zIndex.modal + 20 }}
      ModalProps={{ sx: { zIndex: (theme) => theme.zIndex.modal + 20 } }}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 560, md: 600 },
          maxWidth: '100vw',
          bgcolor: '#fbfaf7',
          zIndex: 'inherit',
        },
      }}
    >
      <Box sx={{ p: { xs: 2.2, sm: 3 }, minHeight: '100%' }}>
        <Stack direction="row" justifyContent="space-between" gap={2} alignItems="flex-start">
          <Box minWidth={0}>
            <Stack direction="row" spacing={1} alignItems="center">
              <AutoAwesomeRoundedIcon sx={{ color: '#d07b08' }} />
              <Typography variant="h3" sx={{ fontSize: 24 }}>Research & Draft</Typography>
            </Stack>

            <Typography color="text.secondary" variant="body2" sx={{ mt: 0.6 }}>
              {item?.subject || 'Opportunity'}
              {research?.recipientEmail ? ` · ${research.recipientEmail}` : ''}
            </Typography>
          </Box>

          <IconButton onClick={onClose} aria-label="Close Research & Draft">
            <CloseRoundedIcon />
          </IconButton>
        </Stack>

        {loading && (
          <Stack alignItems="center" py={8}>
            <CircularProgress size={30} />
            <Typography color="text.secondary" sx={{ mt: 2 }}>Reading the conversation…</Typography>
          </Stack>
        )}

        {!loading && error && <Alert severity="error" sx={{ mt: 3 }}>{error}</Alert>}

        {!loading && !error && research && (
          <Stack spacing={2} sx={{ mt: 3 }}>
            {threadBrief ? (
              <ThreadContextCard brief={threadBrief} />
            ) : (
              <Alert severity="info">
                This saved research run predates thread-first Research & Draft. Run Research & Draft again to capture the open loop and next step directly from the email conversation.
              </Alert>
            )}

            {threadBrief?.usable && (
              <Button
                size="small"
                variant="text"
                startIcon={<ContentCopyRoundedIcon />}
                onClick={copyThreadContext}
                sx={{ alignSelf: 'flex-start', px: 0.5 }}
              >
                {copied ? 'Copied' : 'Copy thread context'}
              </Button>
            )}

            {contactHistoryBrief && (
              <>
                <Divider />
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid rgba(26,29,49,.09)',
                    bgcolor: contactHistoryBrief.usable ? 'rgba(141,149,255,.055)' : '#fff',
                  }}
                >
                  <Stack direction="row" spacing={.8} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography fontWeight={900}>Relevant past conversations</Typography>
                    <Chip size="small" variant="outlined" label="Same contact" />
                    {contactHistoryBrief.threadCount > 0 && (
                      <Chip size="small" variant="outlined" label={`${contactHistoryBrief.threadCount} other thread${contactHistoryBrief.threadCount === 1 ? '' : 's'}`} />
                    )}
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: .45 }}>
                    Supplemental context from other recent emails with this person. The current thread still takes priority.
                  </Typography>

                  {contactHistoryBrief.usable ? (
                    <Stack spacing={1} sx={{ mt: 1.4 }}>
                      {contactHistoryBrief.relationshipContext && (
                        <Typography variant="body2" sx={{ lineHeight: 1.55 }}>{contactHistoryBrief.relationshipContext}</Typography>
                      )}
                      {(contactHistoryBrief.facts || []).slice(0, 5).map((fact, index) => (
                        <Typography key={`${fact}-${index}`} variant="body2">• {fact}</Typography>
                      ))}
                      {(contactHistoryBrief.cautions || []).length > 0 && (
                        <Alert severity="info" sx={{ py: .2 }}>
                          {(contactHistoryBrief.cautions || []).slice(0, 3).join(' · ')}
                        </Alert>
                      )}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1.2 }}>
                      Nudge checked other recent conversations but did not find context that should affect this follow-up.
                    </Typography>
                  )}
                </Box>
              </>
            )}

            <Divider />

            <Box>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ sm: 'center' }}
                gap={1.2}
              >
                <Box>
                  <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography fontWeight={900}>Optional external context</Typography>
                    <Chip size="small" variant="outlined" label="Optional" />
                    {providersUsed.length > 0 && (
                      <Chip size="small" color="success" variant="outlined" label={`${providersUsed.length} source${providersUsed.length === 1 ? '' : 's'} used`} />
                    )}
                  </Stack>

                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.45, lineHeight: 1.55 }}>
                    Apollo, Cleanlist and web research can add useful context, but they are not required for Research & Draft.
                  </Typography>
                </Box>

                {externalAvailable && (
                  <Button
                    size="small"
                    color="inherit"
                    endIcon={showExternal ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                    onClick={() => setShowExternal((value) => !value)}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    {showExternal ? 'Hide details' : 'View details'}
                  </Button>
                )}
              </Stack>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                External providers use your connected API keys
                {externalMeta.separateProviderCostsMayApply !== false ? ' and their own usage/pricing may apply.' : '.'}
              </Typography>

              {!externalAvailable && (
                <Alert severity="info" sx={{ mt: 1.5 }}>
                  No external source was used. That is fine — this draft can be based entirely on the email conversation.
                </Alert>
              )}

              <Collapse in={showExternal && externalAvailable}>
                <Stack spacing={1.2} sx={{ mt: 1.6 }}>
                  {externalBrief?.usable && (
                    <Box
                      sx={{
                        p: 1.7,
                        borderRadius: 1.6,
                        bgcolor: 'rgba(141,149,255,.07)',
                        border: '1px solid rgba(141,149,255,.18)',
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" fontWeight={800}>EXTERNAL ENHANCEMENT</Typography>
                      {externalBrief.angle && <Typography sx={{ mt: 0.4, fontWeight: 800 }}>{externalBrief.angle}</Typography>}
                      {externalBrief.reason && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{externalBrief.reason}</Typography>}
                      {cleanFacts(externalBrief.facts).length > 0 && (
                        <Stack spacing={0.45} sx={{ mt: 1 }}>
                          {cleanFacts(externalBrief.facts).map((fact, index) => (
                            <Typography key={`${fact}-${index}`} variant="body2">• {fact}</Typography>
                          ))}
                        </Stack>
                      )}
                    </Box>
                  )}

                  {providerResults.map((provider, index) => {
                    const meta = providerStatusMeta(provider);
                    const fields = providerFields(provider.data);
                    const providerSources = Array.isArray(provider.sources) ? provider.sources : [];

                    return (
                      <Box
                        key={`${provider.provider}-${provider.status}-${index}`}
                        sx={{
                          border: '1px solid rgba(26,29,49,.09)',
                          borderRadius: 1.6,
                          bgcolor: '#fff',
                          p: 1.6,
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" gap={1.5} alignItems="center">
                          <Typography fontWeight={850}>{provider.label || providerLabel(provider.provider)}</Typography>
                          <Chip
                            size="small"
                            color={meta.tone === 'default' ? undefined : meta.tone}
                            icon={meta.icon}
                            label={meta.label}
                          />
                        </Stack>

                        {provider.error && provider.status !== 'not_connected' && (
                          <Alert severity={provider.status === 'skipped' ? 'info' : 'warning'} sx={{ mt: 1.1, py: 0.1 }}>
                            {provider.error}
                          </Alert>
                        )}

                        {fields.length > 0 && (
                          <Box
                            sx={{
                              mt: 1.2,
                              display: 'grid',
                              gridTemplateColumns: 'minmax(110px,.5fr) minmax(0,1fr)',
                              columnGap: 1.3,
                              rowGap: 0.65,
                            }}
                          >
                            {fields.map((field) => [
                              <Typography key={`${field.key}-label`} variant="caption" color="text.secondary" fontWeight={750}>
                                {field.label}
                              </Typography>,
                              <Typography key={`${field.key}-value`} variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                                {field.value}
                              </Typography>,
                            ])}
                          </Box>
                        )}

                        {providerSources.slice(0, 4).map((source, sourceIndex) => (
                          <Button
                            key={`${source.url || source.title}-${sourceIndex}`}
                            component="a"
                            href={source.url || '#'}
                            target="_blank"
                            rel="noreferrer"
                            variant="text"
                            size="small"
                            endIcon={<OpenInNewRoundedIcon />}
                            sx={{ display: 'flex', justifyContent: 'flex-start', px: 0, mt: 0.7, textAlign: 'left' }}
                          >
                            {source.title || source.url || 'Open source'}
                          </Button>
                        ))}
                      </Box>
                    );
                  })}

                  {sources.length > 0 && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={800}>WEB SOURCES</Typography>
                      <Stack spacing={0.8} sx={{ mt: 0.7 }}>
                        {sources.slice(0, 5).map((source, index) => (
                          <Button
                            key={`${source.url || source.title}-${index}`}
                            component="a"
                            href={source.url || '#'}
                            target="_blank"
                            rel="noreferrer"
                            variant="text"
                            size="small"
                            endIcon={<OpenInNewRoundedIcon />}
                            sx={{ justifyContent: 'flex-start', px: 0, textAlign: 'left' }}
                          >
                            {source.title || source.url || 'Open source'}
                          </Button>
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Stack>
              </Collapse>
            </Box>

            {research?.draft?.created && (
              <>
                <Divider />
                <Box
                  sx={{
                    p: 1.9,
                    borderRadius: 1.7,
                    bgcolor: 'rgba(70,201,142,.07)',
                    border: '1px solid rgba(70,201,142,.18)',
                  }}
                >
                  <Stack direction="row" spacing={0.8} alignItems="center">
                    <CheckCircleOutlineRoundedIcon color="success" fontSize="small" />
                    <Typography fontWeight={900}>Draft created</Typography>
                  </Stack>

                  {research.draft.preview && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.8, whiteSpace: 'pre-wrap', lineHeight: 1.55 }}
                    >
                      {research.draft.preview}
                    </Typography>
                  )}
                </Box>
              </>
            )}
          </Stack>
        )}

        {!loading && !error && !research && (
          <Alert severity="info" sx={{ mt: 3 }}>
            No saved Research & Draft context is available for this opportunity yet.
          </Alert>
        )}
      </Box>
    </Drawer>
  );
}
